import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { PurchaseOrder } from '../../interfaces/procurement/purchaseOrder.interface';
import { useToast } from '../../context/ToastContext';

export const PendingApprovalPurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!id) return;
    PurchaseOrderService.getById(id)
      .then(setPo)
      .catch(e => toast.showApiError(e, 'Failed to load purchase order'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async () => {
    if (!po) return;
    if (!window.confirm('Approve this PO?')) return;
    try {
      setActing(true);
      await PurchaseOrderService.approve(po.id);
      toast.showSuccess('Purchase order approved');
      navigate('/procurement/purchase-orders/pending-approval');
    } catch (e) {
      toast.showApiError(e, 'Failed to approve purchase order');
    } finally {
      setActing(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!po) return;
    try {
      setActing(true);
      setRejectDialogOpen(false);
      await PurchaseOrderService.reject(po.id);
      toast.showSuccess('Purchase order rejected');
      navigate('/procurement/purchase-orders/pending-approval');
    } catch (e) {
      toast.showApiError(e, 'Failed to reject purchase order');
    } finally {
      setActing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!po) return <Typography sx={{ p: 3 }}>Purchase order not found</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">{po.poNumber}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip label={po.status} color="warning" size="small" />
            <Typography variant="body2" color="text.secondary">Rev {po.revision}</Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={acting}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setRejectDialogOpen(true)}
            disabled={acting}
          >
            Reject
          </Button>
        </Stack>
      </Box>

      {/* Summary */}
      <Paper sx={{ p: 2, mb: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <Typography variant="body2"><strong>Supplier:</strong> {po.supplierId}</Typography>
        <Typography variant="body2"><strong>Order Date:</strong> {po.orderDate}</Typography>
        <Typography variant="body2"><strong>Expected Date:</strong> {po.expectedDate ?? '-'}</Typography>
        <Typography variant="body2"><strong>Tax Amount:</strong> {po.taxAmount}</Typography>
        <Typography variant="body2" sx={{ gridColumn: '1 / -1' }}>
          <strong>Notes:</strong> {po.notes ?? '-'}
        </Typography>
      </Paper>

      {/* Lines */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item ID</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Unit Price</TableCell>
              <TableCell>Line Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {po.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>{l.itemId}</TableCell>
                <TableCell>{l.quantity}</TableCell>
                <TableCell>{l.unitPrice}</TableCell>
                <TableCell>{l.lineTotal}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals */}
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        <Typography>Subtotal: {po.subtotal}</Typography>
        <Typography>Tax: {po.taxAmount}</Typography>
        <Typography variant="h6">Total: {po.totalAmount}</Typography>
      </Paper>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Reject Purchase Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Reject this Purchase Order? This will mark it as REJECTED. Cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRejectConfirm}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingApprovalPurchaseOrderDetail;
